import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Seller } from '@tips-crm/shared';
import {
  createSeller,
  deleteSeller,
  fetchSellers,
  updateSeller,
} from '../../api/endpoints.js';
import { errorMessage } from '../../api/client.js';
import { PageHeader } from '../../components/PageHeader.js';
import { DataTable, type Column } from '../../components/DataTable.js';
import { Modal } from '../../components/Modal.js';

export function SellersPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Seller | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const query = useQuery({ queryKey: ['sellers'], queryFn: fetchSellers });

  const saveMutation = useMutation({
    mutationFn: () =>
      editing
        ? updateSeller(editing.id, { name, status })
        : createSeller({ name, status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sellers'] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSeller(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sellers'] }),
  });

  function openCreate() {
    setEditing(null);
    setName('');
    setStatus('active');
    setCreating(true);
  }

  function openEdit(s: Seller) {
    setEditing(s);
    setName(s.name);
    setStatus(s.status);
    setCreating(true);
  }

  function closeForm() {
    setCreating(false);
    setEditing(null);
  }

  const columns = useMemo<Column<Seller>[]>(
    () => [
      { key: 'name', header: 'Name', render: (s) => s.name },
      {
        key: 'status',
        header: 'Status',
        render: (s) => <span className={`badge badge--status-${s.status}`}>{s.status}</span>,
      },
      {
        key: 'created',
        header: 'Created',
        render: (s) => new Date(s.created_at).toLocaleDateString(),
      },
      {
        key: 'actions',
        header: '',
        render: (s) => (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => openEdit(s)}>
              Edit
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                if (confirm(`Delete seller "${s.name}"?`)) deleteMutation.mutate(s.id);
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [deleteMutation],
  );

  return (
    <div className="page">
      <PageHeader
        title="Sellers"
        subtitle="Manage seller tenants."
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New seller
          </button>
        }
      />

      {deleteMutation.isError && <div className="form-error">{errorMessage(deleteMutation.error)}</div>}

      <DataTable
        columns={columns}
        rows={query.data}
        rowKey={(s) => s.id}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => query.refetch()}
        emptyMessage="No sellers yet. Create one to get started."
      />

      <Modal open={creating} title={editing ? 'Edit seller' : 'New seller'} onClose={closeForm}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
        >
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="field__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="field">
            <span className="field__label">Status</span>
            <select
              className="field__input"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </label>

          {saveMutation.isError && <div className="form-error">{errorMessage(saveMutation.error)}</div>}

          <div className="form__actions">
            <button type="button" className="btn btn--ghost" onClick={closeForm}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={saveMutation.isPending || !name}>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
